import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Mail } from "lucide-react";
import { toast } from "sonner";

// Settings-page card for GET/PUT /settings/dispatch-notifications:
//   * billing team — emailed when a customer DC is raised, when it is
//     dispatched (DC PDF attached) and when its Tax Invoice is generated
//     (invoice PDF attached); "auto_tax_invoice" raises that invoice on dispatch
//   * stores — emailed when a Sales Order is raised (pack), revised
//     (re-check packing) or cancelled (unpack)
//   * sales team — in the To of the Sales Order email sent to the customer
//     when it is raised or revised
//   * order created — a "New Transfer Order / Sales Order Created" notice,
//     sent once when each order is first saved (ramji@ / gajanan@ by default)
// Each list takes any number of addresses, one per line or comma separated.
const toText = (list) => (list || []).join("\n");
const toList = (text) => text.split(/[\n,;]/).map((x) => x.trim()).filter(Boolean);

export default function DispatchNotificationSettings() {
  const [cfg, setCfg] = useState(null);
  const [billing, setBilling] = useState("");
  const [stores, setStores] = useState("");
  const [salesTeam, setSalesTeam] = useState("");
  const [orderCreated, setOrderCreated] = useState("");
  const [busy, setBusy] = useState(false);

  const apply = (data) => {
    setCfg(data);
    setBilling(toText(data.billing_emails));
    setStores(toText(data.store_emails));
    setSalesTeam(toText(data.sales_order_emails));
    setOrderCreated(toText(data.order_created_emails));
  };
  useEffect(() => {
    api.get("/settings/dispatch-notifications").then((r) => apply(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings/dispatch-notifications", {
        ...cfg, billing_emails: toList(billing), store_emails: toList(stores),
        sales_order_emails: toList(salesTeam), order_created_emails: toList(orderCreated),
      });
      apply(data);
      toast.success("Notification emails saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  if (!cfg) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6 max-w-2xl" data-testid="dispatch-notifications-section">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-md bg-blue-50 flex items-center justify-center">
          <Mail className="w-5 h-5 text-blue-700" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "Manrope" }}>Dispatch &amp; Packing Notifications</h3>
          <p className="text-xs text-slate-500">Email the billing team when a DC is raised or dispatched and when its Tax Invoice is generated, and stores when a Sales Order needs packing or unpacking. Enter one address per line (or comma separated).</p>
        </div>
      </div>

      <div className="space-y-5">
        <div>
          <div className="flex items-center justify-between">
            <Label>Billing team — Delivery Challan &amp; Tax Invoice</Label>
            <Switch checked={cfg.billing_enabled} onCheckedChange={(v) => setCfg({ ...cfg, billing_enabled: v })} data-testid="dn-billing-enabled" />
          </div>
          <Textarea rows={3} className="mt-1 font-mono text-xs" placeholder={"billing@canares.com\naccounts@canares.com"}
            value={billing} onChange={(e) => setBilling(e.target.value)} data-testid="dn-billing-emails" />
          <label className="mt-1.5 flex items-center gap-2 text-xs text-slate-600">
            <Switch checked={cfg.attach_dc_pdf} onCheckedChange={(v) => setCfg({ ...cfg, attach_dc_pdf: v })} data-testid="dn-attach-pdf" />
            Attach the DC PDF
          </label>
          <label className="mt-1.5 flex items-center gap-2 text-xs text-slate-600">
            <Switch checked={cfg.auto_tax_invoice} onCheckedChange={(v) => setCfg({ ...cfg, auto_tax_invoice: v })} data-testid="dn-auto-invoice" />
            Generate the Tax Invoice automatically when a DC is dispatched
          </label>
          <label className="mt-1.5 flex items-center gap-2 text-xs text-slate-600">
            <Switch checked={cfg.invoice_enabled} onCheckedChange={(v) => setCfg({ ...cfg, invoice_enabled: v })} data-testid="dn-invoice-enabled" />
            Email each generated Tax Invoice
          </label>
          <label className="mt-1.5 flex items-center gap-2 text-xs text-slate-600">
            <Switch checked={cfg.attach_invoice_pdf} onCheckedChange={(v) => setCfg({ ...cfg, attach_invoice_pdf: v })} data-testid="dn-attach-invoice-pdf" />
            Attach the Tax Invoice PDF
          </label>
          <p className="text-[11px] text-slate-500 mt-1">Sent when a DC is raised, when it is dispatched, and when its Tax Invoice is generated (with a verification check against the DC).</p>
        </div>

        <div>
          <div className="flex items-center justify-between">
            <Label>Stores — Sales Order packing / unpacking</Label>
            <Switch checked={cfg.store_enabled} onCheckedChange={(v) => setCfg({ ...cfg, store_enabled: v })} data-testid="dn-store-enabled" />
          </div>
          <Textarea rows={3} className="mt-1 font-mono text-xs" placeholder={"stores@canares.com\nfg.store@canares.com"}
            value={stores} onChange={(e) => setStores(e.target.value)} data-testid="dn-store-emails" />
          <p className="text-[11px] text-slate-500 mt-1">Sent when a Sales Order is raised (pack from FG stock), revised (re-check packing) or cancelled (unpack).</p>
        </div>

        <div>
          <div className="flex items-center justify-between">
            <Label>Sales team — Sales Order raised</Label>
            <Switch checked={cfg.sales_order_enabled} onCheckedChange={(v) => setCfg({ ...cfg, sales_order_enabled: v })} data-testid="dn-sales-order-enabled" />
          </div>
          <Textarea rows={3} className="mt-1 font-mono text-xs" placeholder={"priyanka@canares.com\nshilpa@canares.com"}
            value={salesTeam} onChange={(e) => setSalesTeam(e.target.value)} data-testid="dn-sales-order-emails" />
          <p className="text-[11px] text-slate-500 mt-1">Added to the To of the Sales Order email (PDF attached) that goes to the customer when a Sales Order is raised or revised. The person who raised the order is Cc'd.</p>
        </div>

        <div>
          <div className="flex items-center justify-between">
            <Label>Order created — new Transfer Order / Sales Order</Label>
            <Switch checked={cfg.order_created_enabled} onCheckedChange={(v) => setCfg({ ...cfg, order_created_enabled: v })} data-testid="dn-order-created-enabled" />
          </div>
          <Textarea rows={3} className="mt-1 font-mono text-xs" placeholder={"ramji@canares.com\ngajanan@canares.com"}
            value={orderCreated} onChange={(e) => setOrderCreated(e.target.value)} data-testid="dn-order-created-emails" />
          <p className="text-[11px] text-slate-500 mt-1">A "New Transfer Order / Sales Order Created" email with the order details, sent once when each Transfer Order is saved and when Production Approval raises its Sales Order. Not sent for edits or revisions.</p>
        </div>
      </div>

      <div className="pt-4">
        <Button onClick={save} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="dn-save-btn">{busy ? "Saving…" : "Save"}</Button>
      </div>
    </div>
  );
}
